import React from 'react';
import { ArrowLeft, CalendarDays, Clock, MapPin, Mic, Pause, Upload, Users } from 'lucide-react';
import { formattaMs } from '../../utils/verbaliTrascrizione';
import VideoGuidaVerbali from './VideoGuidaVerbali';

export interface DatiTestata {
  nome: string;
  tipologia?: string | null;
  dataEvento?: string;
  oraInizio?: string;
  oraFine?: string;
  luogo?: string;
  presenti?: number;
  invitati?: number;
}

interface VerbaleTestataProps {
  evento: DatiTestata;
  bozza: boolean;
  /** Registrazione e caricamento audio (piano 2 dei verbali): spenti finché è falso. */
  audioDisponibile: boolean;
  onTorna: () => void;
  onCaricaAudio?: () => void;
  onRegistra?: () => void;
  /** Millisecondi della registrazione in corso (null = non registra): il pulsante diventa «In registrazione · m:ss». */
  inRegistrazione?: number | null;
  /** Registrazione in pausa: «In pausa · m:ss». */
  inPausa?: boolean;
  /** Pulsante «Video guida» (solo per l'autore). */
  videoGuida?: boolean;
}

const dataLunga = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const testo = d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return testo.charAt(0).toUpperCase() + testo.slice(1);
};

const orario = (inizio?: string, fine?: string) => {
  if (inizio && fine) return `${inizio}–${fine}`;
  return inizio || '';
};

/** Testata verde del verbale: tipologia, stato, nome, data · orario · luogo · presenze. */
const VerbaleTestata: React.FC<VerbaleTestataProps> = ({
  evento,
  bozza,
  audioDisponibile,
  onTorna,
  onCaricaAudio,
  onRegistra,
  inRegistrazione = null,
  inPausa = false,
  videoGuida = false,
}) => {
  const registra = typeof inRegistrazione === 'number';
  const data = dataLunga(evento.dataEvento);
  const ore = orario(evento.oraInizio, evento.oraFine);
  const invitati = evento.invitati ?? 0;
  const presenti = evento.presenti ?? 0;

  return (
    <header className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-900 via-emerald-700 to-teal-600 px-5 py-5 text-white shadow-lg shadow-emerald-900/25 sm:px-7 sm:py-6">
      <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-teal-300/20 blur-3xl" />

      <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {evento.tipologia && (
              <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-semibold tracking-wide ring-1 ring-inset ring-white/20">
                {evento.tipologia}
              </span>
            )}
            {bozza ? (
              <span className="inline-flex items-center rounded-full bg-amber-300/25 px-2.5 py-0.5 text-xs font-semibold text-amber-50 ring-1 ring-inset ring-amber-200/40">
                Bozza
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                Pubblicato
              </span>
            )}
          </div>

          <h1 className="text-base font-semibold leading-snug tracking-tight text-white sm:text-lg">
            <span className="text-emerald-100/80">Verbale · </span>{evento.nome}
          </h1>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-emerald-50/90">
            {data && (
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5 opacity-80" aria-hidden="true" />{data}
              </span>
            )}
            {ore && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 opacity-80" aria-hidden="true" />{ore}
              </span>
            )}
            {evento.luogo && (
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0 opacity-80" aria-hidden="true" />
                <span className="truncate">{evento.luogo}</span>
              </span>
            )}
            {invitati > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 opacity-80" aria-hidden="true" />
                {presenti} presenti su {invitati} invitati
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 md:justify-end">
          {audioDisponibile && (
            <>
              <button
                type="button"
                onClick={onCaricaAudio}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/35 bg-white/10 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <Upload className="h-4 w-4" aria-hidden="true" />
                Carica audio
              </button>
              <button
                type="button"
                onClick={onRegistra}
                className="inline-flex items-center gap-2.5 rounded-full bg-white py-1.5 pl-1.5 pr-4 text-sm font-bold text-red-700 shadow-md shadow-black/20 transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <span className={`relative grid h-7 w-7 place-items-center rounded-full text-white ${registra && inPausa ? 'bg-amber-500' : 'bg-red-600'}`}>
                  {!(registra && inPausa) && (
                    <span className="absolute inset-0 animate-ping rounded-full bg-red-500/40 motion-reduce:animate-none" aria-hidden="true" />
                  )}
                  {registra && inPausa
                    ? <Pause className="relative h-3.5 w-3.5" aria-hidden="true" />
                    : <Mic className="relative h-3.5 w-3.5" aria-hidden="true" />}
                </span>
                {registra ? (
                  <span className="tabular-nums">
                    {inPausa ? 'In pausa' : 'In registrazione'} · {formattaMs(inRegistrazione as number)}
                  </span>
                ) : 'Registra riunione'}
              </button>
            </>
          )}
          {videoGuida && <VideoGuidaVerbali variante="testata" />}
          <button
            type="button"
            onClick={onTorna}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-white/10 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Torna all'evento
          </button>
        </div>
      </div>
    </header>
  );
};

export default VerbaleTestata;
